/**
 * AdvertiserMultiSelect
 *
 * Paginated, searchable multi-select drop-down for advertisers. Delegates
 * scoping entirely to the API — `advertiserApi.getAdvertisers()` hits the
 * admin path for admin sessions (all advertisers) and the user path for
 * regular users (only those they can access). No FE-side role filtering
 * happens here; the component just renders whatever the BE returns for
 * the current JWT.
 *
 * Extracted from `CreateBrandKitModal` so the Extract-from-file modal
 * can reuse the same picker without duplicating the pagination /
 * search / infinite-scroll machinery.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Building2, ChevronDown, Search, Loader2, Check } from 'lucide-react'
import { advertiserApi, Advertiser } from '../../lib/api/advertisers'

interface AdvertiserMultiSelectProps {
  selected: Advertiser[]
  onChange: (advertisers: Advertiser[]) => void
  disabled?: boolean
  /** Custom placeholder for the closed state. */
  placeholder?: string
}

export function AdvertiserMultiSelect({
  selected,
  onChange,
  disabled,
  placeholder = 'Select advertisers...',
}: AdvertiserMultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false)
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useEffect(() => {
    if (isOpen && searchInputRef.current) searchInputRef.current.focus()
  }, [isOpen])

  const loadAdvertisers = useCallback(async (page: number, query: string, append = false) => {
    append ? setIsLoadingMore(true) : setIsLoading(true)
    try {
      const { advertisers: data, meta } = await advertiserApi.getAdvertisers({
        page,
        per_page: 20,
        q: query || undefined,
      })
      setAdvertisers(append ? (prev) => [...prev, ...data] : data)
      setCurrentPage(meta.current_page)
      setHasMore(meta.current_page < meta.total_pages)
    } catch (err) {
      console.error('Failed to load advertisers:', err)
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen && advertisers.length === 0) loadAdvertisers(1, '')
  }, [isOpen, advertisers.length, loadAdvertisers])

  const handleSearchChange = (val: string) => {
    setSearchQuery(val)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => loadAdvertisers(1, val), 300)
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const t = e.target as HTMLDivElement
    if (t.scrollHeight - t.scrollTop - t.clientHeight < 50 && hasMore && !isLoadingMore) {
      loadAdvertisers(currentPage + 1, searchQuery, true)
    }
  }

  const toggle = (adv: Advertiser) => {
    const exists = selected.find((s) => s.id === adv.id)
    onChange(exists ? selected.filter((s) => s.id !== adv.id) : [...selected, adv])
  }

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    },
    [],
  )

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left border rounded-lg transition-colors ${
          disabled
            ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60'
            : 'bg-white dark:bg-gray-900 hover:border-gray-400 dark:hover:border-gray-600 cursor-pointer'
        } ${isOpen ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-gray-200 dark:border-gray-800'}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {selected.length > 0 ? (
            <span className="text-sm text-gray-900 dark:text-white truncate">
              {selected.map((s) => s.name).join(', ')}
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">{placeholder}</span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search advertisers..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
              />
            </div>
          </div>
          <div onScroll={handleScroll} className="max-h-56 overflow-y-auto scrollbar-thin">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : advertisers.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500">No advertisers found</div>
            ) : (
              <>
                {advertisers.map((adv) => {
                  const isSel = selected.some((s) => s.id === adv.id)
                  return (
                    <button
                      key={adv.id}
                      type="button"
                      onClick={() => toggle(adv)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                        isSel ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      <div
                        className={`w-4 h-4 flex-shrink-0 border rounded ${
                          isSel
                            ? 'bg-primary-600 border-primary-600 flex items-center justify-center'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {isSel && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span
                        className={`text-sm truncate ${
                          isSel ? 'text-primary-700 dark:text-primary-400 font-medium' : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {adv.name}
                      </span>
                    </button>
                  )
                })}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
